'use client'

import { memo, useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { Eye, ArrowRight } from 'lucide-react'
import type { HeroDemo } from '@kiltechco/dday-templates/demos'
import { PageLoader } from '@/components/ui/Loading'

interface DesignCardProps {
  design: HeroDemo
  onClick: (design: HeroDemo) => void
  onPreview: (design: HeroDemo) => void
  /** Set true for above-the-fold images to prioritize loading */
  priority?: boolean
}

export default memo(function DesignCard({
  design,
  onClick,
  onPreview,
  priority = false,
}: DesignCardProps) {
  const [showMobileActions, setShowMobileActions] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isSelecting, setIsSelecting] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // Close overlay when clicking outside the card
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setShowMobileActions(false)
      }
    }

    if (showMobileActions) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMobileActions])

  const handleSelect = () => {
    setIsSelecting(true)
    onClick(design)
  }

  const handlePreview = () => {
    onPreview(design)
  }

  const handleCardClick = () => {
    // On mobile, first tap shows the actions
    setShowMobileActions(true)
  }

  // Get thumbnail from design metadata (provided by package)
  const thumbnailUrl = design.thumbnailUrl || '/images/default-thumbnail.png'

  // Track whether overlay buttons should be interactive
  const isOverlayVisible = showMobileActions || isHovered

  return (
    <div
      ref={cardRef}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative bg-card rounded-xl border border-border p-4 hover:border-primary hover:shadow-lg transition-all duration-300"
    >
      <div className="flex flex-col items-center gap-3">
        {/* Preview Thumbnail */}
        <div
          className="relative rounded-xl overflow-hidden shadow-lg"
          style={{
            width: '100px',
            height: '210px',
          }}
        >
          {/* Explicit loading/fetchPriority needed because DigitalDesignGrid
             loads designs via dynamic import — priority alone generates preload
             links too late when images are client-rendered, not SSR. */}
          <Image
            src={thumbnailUrl}
            alt={design.label}
            fill
            className="object-cover"
            sizes="100px"
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            quality={80}
            unoptimized
          />
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors text-center">
          {design.label}
        </h3>
      </div>

      {/* Desktop Hover Overlay + Mobile/Tablet Tap Overlay */}
      <div
        onClick={(e) => {
          e.stopPropagation()
          // On mobile/tablet, clicking the overlay (not buttons) hides it
          setShowMobileActions(false)
        }}
        className={`absolute inset-0 rounded-xl bg-black/65 transition-opacity items-center justify-center backdrop-blur-[2px] ${
          showMobileActions
            ? 'flex opacity-100 pointer-events-auto'
            : 'hidden opacity-0 pointer-events-none md:flex md:group-hover:opacity-100 md:group-hover:pointer-events-auto'
        }`}
        aria-hidden={!isOverlayVisible}
      >
        <div className="flex flex-col gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation()
              handlePreview()
            }}
            tabIndex={isOverlayVisible ? 0 : -1}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white border border-white/30 rounded-lg font-semibold hover:bg-white/20 active:bg-white/30 transition-colors cursor-pointer touch-manipulation"
          >
            <Eye className="w-5 h-5" />
            Demo
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleSelect()
            }}
            tabIndex={isOverlayVisible ? 0 : -1}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 active:bg-primary/80 transition-colors cursor-pointer touch-manipulation"
          >
            <ArrowRight className="w-5 h-5" />
            Select
          </button>
        </div>
      </div>

      {/* Full-screen Page Loader when selecting */}
      {isSelecting &&
        typeof document !== 'undefined' &&
        createPortal(<PageLoader />, document.body)}
    </div>
  )
})
